import React, { useEffect, useRef, useState } from "react";
import {
	PanelBar,
	PanelBarItem,
	PanelBarUtils,
	PanelBarSelectEventArguments,
	MenuItem,
} from "@progress/kendo-react-layout";
import { PageTypePath } from "utils/pages";

import { getMenuApi } from "api/menu";

import { Font, ICON_NAMES } from "utils/constant";
import { useNavigate, useParams } from "react-router";
import { getCatchSignRegisterUserApi } from "api/sign";
import { getUserUniqueStringApi } from "api/administration";
import { useMenuItemStore } from "store/menuItem";
import { UserMenuItem } from "interface/userMenuItem";

const SideMenuBar: React.FC<{
	currentPage: string;
	isCollapsed?: boolean;
}> = ({ currentPage, isCollapsed = false }) => {
	const navigate = useNavigate();
	const { menuItems, setMenuItem } = useMenuItemStore();
	const [selectedItem, setSelectedItem] = useState({ id: "", expandId: [""] });
	const [menuData, setMenuData] = React.useState<UserMenuItem[]>([]);
	const [flyoutOpenId, setFlyoutOpenId] = useState<string | null>(null);
	const [flyoutTop, setFlyoutTop] = useState<number>(0);
	const flyoutRef = useRef<HTMLDivElement>(null);

	let { path } = useParams();

	// Close flyout when clicking outside
	useEffect(() => {
		if (!flyoutOpenId) return;
		const handleClick = (e: MouseEvent) => {
			if (flyoutRef.current && !flyoutRef.current.contains(e.target as Node)) {
				setFlyoutOpenId(null);
			}
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [flyoutOpenId]);

	// Close flyout when sidebar expands
	useEffect(() => {
		if (!isCollapsed) setFlyoutOpenId(null);
	}, [isCollapsed]);

	const handleOnselect = (e: PanelBarSelectEventArguments) => {
		const path = e.target.props.path;
		const id = e.target.props.id;

		setSelectedItem({
			expandId: e.expandedItems,
			id: e.target.props.id as any,
		});

		if (path === PageTypePath.Home) {
			navigate(`/`);
			return;
		}

		if (id === "337") {
			const newWindow = window.open('about:blank', '_blank');

			if (!newWindow) {
				console.error('Popup window could not be opened. Popup may be blocked.');
				return;
			}

			getCatchSignRegisterUserApi()
				.then((response) => {
					if (newWindow) {
						newWindow.location.href = response;
					}
				})
				.catch((error) => {
					if (newWindow) {
						newWindow.close();
					}
					console.log(error);
				});

			return;
		}

		if (id === "339" || id === "340" || id === "341") {
			let type = 0;

			if (id === "340") {
				type = 1;
			}
			if (id === "341") {
				type = 2;
			}

			// Open window first, but with about:blank - more reliable
			const newWindow = window.open('about:blank', '_blank');

			if (!newWindow) {
				console.error('Popup window could not be opened. Popup may be blocked.');
				return;
			}

			getUserUniqueStringApi()
				.then((response) => {
					try {
						// Use replace instead of href assignment for better Safari compatibility
						if (newWindow && !newWindow.closed) {
							newWindow.location.replace(path + '?id=' + response + '&type=' + type);
						}
					} catch (e) {
						console.error('Failed to navigate window:', e);
						newWindow?.close();
					}
				})
				.catch((error) => {
					console.error('API Error:', error);
					if (newWindow && !newWindow.closed) {
						newWindow.close();
					}
				});

			return;
		}

		if (path) {
			if (path.startsWith("http")) {
			window.open(path, "_blank");
			} else {
				navigate(`/${path}`);
			}
		}
	};

	useEffect(() => {
		getMenu();
	}, []);

	useEffect(() => {
		handleMenuUpdate();
	}, [menuItems]);

	const getMenu = () => {
		getMenuApi()
			.then((response) => {
				setMenuItem(
					response.map((item) => ({
						...item,
						id: item.id.toString(),
						children: item.children
							? item.children.map((child) => ({
									...child,
								id: child.id.toString(),
								}))
							: null,
					})) as any,
				);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const handleMenuUpdate = () => {
		let data = menuItems;
		try {
			if (path) {
				data.forEach((item) => {
					let expand = false;

					if (item.path === path) {
						item.selected = true;
						setSelectedItem({ id: item.id as any, expandId: [item.id] as any });
					} else {
						if (item.children) {
							item.children.forEach((child) => {
								if (child.path === path) {
									setSelectedItem({
										id: child.id as any,
										expandId: [item.id] as any,
									});
								}
							});
						}
					}
				});
			} else {
				if (data[0] !== undefined) {
					setSelectedItem({
						id: data[0].id as any,
						expandId: [data[0].id] as any,
					});
				} else {
					setSelectedItem({
						id: "0",
						expandId: ["0"],
					});
				}
			}
			setMenuData(data);
		} catch (error) {
			console.log("error", error);
		}
	};

	const handleSelectedData = () => {
		let data = menuData.map((item) => ({
			...item,
			children: item.children
				? item.children.map((child) => ({
						...child,
						children: child.children
							? child.children.map((grandchild) => ({ ...grandchild }))
							: undefined,
					}))
				: undefined,
		}));

		data = data.filter((item) => {
			if (item.children && item.children.length > 0) {
				item.children = item.children.filter((child) => {
					if (child.children && child.children.length > 0) {
						const children = child.children.filter(
							(grandchild) => grandchild.status !== 2,
						);
						child.children = children.length > 0 ? children : undefined;
					}
					return child.status !== 2;
				});
				return item.children.length > 0;
			}
			return item.status !== 2;
		});

		return data.map((item) => {
			if (!path) {
				if (
					item.path === PageTypePath.Home &&
					selectedItem.id === item.id.toString()
				) {
					return {
						...item,
						imageUrl: `/icon/${ICON_NAMES.HomeWhite}.svg`,
					};
				}
			}
			if (selectedItem.id === item.id.toString()) {
				return {
					...item,
					imageUrl: item.imageUrlTransparent,
				};
			}
			return item;
		});
	};

	const handleCollapsedItemClick = (item: UserMenuItem, e: React.MouseEvent<HTMLButtonElement>) => {
		const hasChildren = item.children && item.children.length > 0;
		if (hasChildren) {
			const itemId = String(item.id);
			const rect = e.currentTarget.getBoundingClientRect();
			setFlyoutTop(rect.top);
			setFlyoutOpenId((prev) => (prev === itemId ? null : itemId));
			return;
		}
		setFlyoutOpenId(null);
		if (!item.path) return;
		if (item.path === PageTypePath.Home) {
			navigate("/");
		} else if (item.path.startsWith("http")) {
			window.open(item.path, "_blank");
		} else {
			navigate(`/${item.path}`);
		}
	};

	const handleFlyoutChildClick = (child: UserMenuItem) => {
		setFlyoutOpenId(null);
		if (!child.path) return;
		if (child.path.startsWith("http")) {
			window.open(child.path, "_blank");
		} else {
			navigate(`/${child.path}`);
		}
	};

	const components = PanelBarUtils.mapItemsToComponents(
		handleSelectedData() as any,
	);

	if (isCollapsed) {
		const items = handleSelectedData();
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "8px", paddingRight: "8px", position: "relative" }}>
				{items.map((item: any) => {
					const isOpen = flyoutOpenId === String(item.id);
					const hasChildren = item.children && item.children.length > 0;
					const isSelected =
						(hasChildren && item.children.some((child: any) => child.path === path)) ||
						(!hasChildren && item.path === path);

					const iconSrc = isSelected
						? (item.imageUrlTransparent || item.imageUrl)
						: item.imageUrl;

					return (
						<div key={item.id} style={{ position: "relative" }}>
							<button
								type="button"
								title={item.title}
								aria-label={item.title}
								onClick={(e) => handleCollapsedItemClick(item, e)}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: "44px",
									height: "44px",
									border: "none",
									borderRadius: "8px",
									background: isSelected || isOpen ? "#006980" : "transparent",
									cursor: "pointer",
									padding: 0,
								}}
							>
								{iconSrc ? (
									<img
										src={iconSrc}
										alt={item.title}
										style={{ width: "20px", height: "20px", objectFit: "contain" }}
									/>
								) : (
									<span
										style={{
											width: "20px",
											height: "20px",
											backgroundColor: isSelected || isOpen ? "#fff" : "#232626",
											borderRadius: "2px",
											display: "block",
										}}
									/>
								)}
							</button>

							{isOpen && hasChildren && (
								<div
									ref={flyoutRef}
									style={{
										position: "fixed",
										left: "68px",
										top: flyoutTop,
										background: "#fff",
										borderRadius: "8px",
										boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
										minWidth: "200px",
										zIndex: 3000,
										padding: "6px 0",
									}}
								>
									<div
										style={{
											padding: "8px 16px 6px",
											fontFamily: "Work Sans",
											fontStyle: "normal",
											fontWeight: 400,
											fontSize: "15px",
											letterSpacing: "-0.02em",
											color: "#797e80",
											borderBottom: "1px solid #eee",
											marginBottom: "4px",
										}}
									>
										{item.title}
									</div>
									{item.children.map((child: any) => {
										const isChildSelected =
											selectedItem.id === String(child.id);
										return (
											<button
												key={child.id}
												type="button"
												onClick={() => handleFlyoutChildClick(child)}
												style={{
													display: "block",
													width: "100%",
													textAlign: "left",
													padding: "0 16px",
													border: "none",
													height: "38px",
													borderRadius: "0 12px 12px 0",
													marginTop: "7px",
													marginBottom: "7px",
													background: isChildSelected ? "#006980" : "transparent",
													cursor: "pointer",
													fontFamily: "Work Sans",
													fontStyle: "normal",
													fontWeight: 400,
													fontSize: "15px",
													lineHeight: "18px",
													letterSpacing: "-0.02em",
													color: isChildSelected ? "#fff" : "#232626",
													whiteSpace: "nowrap",
												}}
												onMouseEnter={(e) => {
													if (!isChildSelected)
														e.currentTarget.style.background = "snow";
												}}
												onMouseLeave={(e) => {
													if (!isChildSelected)
														e.currentTarget.style.background = "transparent";
												}}
											>
												{child.title}
											</button>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<PanelBar
			style={{
				top: "4px",
				right: "0px",
				backgroundColor: "transparent",
				fontFamily: Font.WorkSans,
				fontSize: "15px",
				border: "1px solid transparent",
			}}
			onSelect={handleOnselect}
			expandMode="single"
			selected={selectedItem.id}
			expanded={selectedItem.expandId}
			isControlled={true}
			keepItemsMounted={true}
			children={components}
		/>
	);
};

export default SideMenuBar;
